import { Behavior } from './Behavior';
import { ContentType, LessonMode, PartialScoringType, QuestionFeedbackState } from '../../Constants';
import UtilsService from '../services/UtilsService';
import questionFeedbackManager from '../managers/QuestionFeedbackManager';

export class MultiselectBehavior extends Behavior {
  checkForValidResponse = (responseAnswer) => {
    // The answer is valid if any of the answers are selected
    return responseAnswer.answers.some(({ isCorrect }) => isCorrect);
  }

  setCorrectAnswer = (state, model) => {
    const response = UtilsService.safeMobxClone(model.validation);

    response.selectedIncorrectList = UtilsService.safeMobxClone([]);
    response.omittedList = UtilsService.safeMobxClone([]);
    response.trueCorrectList = UtilsService.safeMobxClone([]);

    state.setCurrentResponse(response);
  }

  resetStudentAnswer = (state) => {
    state.setCurrentResponse(UtilsService.safeMobxClone(state.cachedResponseAnswer));
  }

  setResponseData = (data, responseAnswer, model) => {
    // data is an id and checked of one of the answers. We want to set the isCorrect property to true on that answer
    const { action, id, checked } = data;
    const answer = responseAnswer.answers.find(({ id: answerId }) => answerId === id);
    if (action === 'select') {
      answer.isCorrect = checked;
      answer.isEliminated = false;
    } else if (action === 'eliminate') {
      answer.isEliminated = !answer.isEliminated;
    }
  }

  getInitializedResponse = (lessonElementId, model) => {
    return {
      answers: model.answers.map(({ id }) => ({ id, isCorrect: false, isEliminated: false })),
      lessonElementId,
      selectedIncorrectList: [],
      omittedList: [],
      trueCorrectList: []
    };
  }

  isAutoScored = (model) => {
    return (ContentType.getAutoscore(model.type) &&
      !model.survey && !model.unscored);
  }

  getCorrectAnswersText = (model) => {
    // Finds the joins an array of all answer text that are correct
    const correctAnswerTexts = (model.validation?.answers || []).reduce((acc, { id, isCorrect }) => {
      if (isCorrect) {
        const answerText = model.answers.find(({ id: answerId }) => answerId === id).text;
        acc.push(answerText);
      }
      return acc;
    }, []);
    return `<div>${correctAnswerTexts.join('')}</div>`;
  }

  getQuestionFeedbackText = (model, correctFeedback, incorrectFeedback, correctFeedbackBody, incorrectFeedbackBody, mode, questionIsCorrect, currentAttemptCount, { lessonElementState }) => {
    let numberOmitted = 0;
    let numberSelectedIncorrect = 0;
    let correctCount = 0;
    if (lessonElementState && lessonElementState.currentResponseAnswer) {
      numberOmitted = (lessonElementState.currentResponseAnswer.omittedList) ? lessonElementState.currentResponseAnswer.omittedList.length : 0;
      numberSelectedIncorrect = (lessonElementState.currentResponseAnswer.selectedIncorrectList) ? lessonElementState.currentResponseAnswer.selectedIncorrectList.length : 0;
      correctCount = (lessonElementState.currentResponseAnswer.trueCorrectList) ? lessonElementState.currentResponseAnswer.trueCorrectList.length : 0;
    }
    let textBody = '';
    if (mode === LessonMode.PREVIEW || mode === LessonMode.ITEM_PREVIEW || mode === LessonMode.STUDENT_PREVIEW || mode === LessonMode.PUBLISHER_PREVIEW) {
      textBody += UtilsService.previewWarning();
    } else {
      if (typeof currentAttemptCount !== 'undefined' && currentAttemptCount !== -1 && currentAttemptCount > 1) {
        textBody += UtilsService.saveWarning();
      } else {
        textBody += UtilsService.saveCommit();
      }
    }
    
    let textHeader = '';
    let attemptsText = '';
    
    // Add attempts remaining text for practice mode when answer is incorrect or partially correct
    if (questionFeedbackManager.isPractice && 
        (questionIsCorrect === QuestionFeedbackState.INCORRECT || questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) &&
        model.lessonElementId) {
      attemptsText = questionFeedbackManager.getAttemptsRemainingText(model.lessonElementId);
    }
    
    const showPartialMessage = (questionIsCorrect === QuestionFeedbackState.PARTIAL_CORRECT) || (questionIsCorrect === QuestionFeedbackState.INCORRECT);
    if (questionIsCorrect === QuestionFeedbackState.CORRECT) {
      textHeader = correctFeedback;
      if (correctFeedbackBody) {
        textBody = `${correctFeedbackBody} ${textBody}`;
      }
    } else if ((showPartialMessage) && (model.correctAnswerCount > 1) && (correctCount > 0)) {
      textHeader = `${UtilsService.partiallyCorrect()}`;
      let custom = '';
      if (incorrectFeedbackBody) {
        custom = `${incorrectFeedbackBody }<br/>`;
      }
      if (numberOmitted > 0 && numberSelectedIncorrect === 0) {
        textBody = `${UtilsService.partiallyCorrectSelectPrompt()}.<br/>${ custom }${textBody}`;
      } else {
        textBody = `${UtilsService.partiallyCorrectSelectPrompt2()}.<br/>${ custom }${textBody}`;
      }
    } else {
      textHeader = incorrectFeedback;
      if (incorrectFeedbackBody) {
        textBody = `${incorrectFeedbackBody}<br/> ${textBody}`;
      }
    }

    // Add attempts text after the header if it exists
    if (attemptsText) {
      textHeader += attemptsText;
    }

    let finalText = '<div class=\'question-feedback-header\'>';
    finalText += textHeader;
    finalText += '</div>';
    finalText += '<div class=\'question-feedback-body\'>';
    finalText += textBody;
    finalText += '</div>';

    return finalText;
  }

  isAnswerCorrect = (answerId, lessonElementState, model, isPracticeRunning) => {
    const response = lessonElementState.currentResponseAnswer;
    const answer = response.answers.find((element) => element.id === answerId);
    if (isPracticeRunning) {
      if (answer && response.selectedIncorrectList && response.omittedList && response.trueCorrectList) {
        if (answer.isCorrect && response.selectedIncorrectList.indexOf(answer.id) > -1) {
          return false;
        }
        if (!answer.isCorrect && response.omittedList.indexOf(answer.id) > -1) {
          return null;
        }
        if (answer.isCorrect && response.trueCorrectList.indexOf(answer.id) > -1) {
          return true;
        }
      }
    } else {
      const correctAnswers = model.validation?.answers;
      if (!correctAnswers) return null;
      const correctAnswer = correctAnswers.find((element) => element.id === answerId);
      if (correctAnswer && answer && correctAnswer.isCorrect && answer.isCorrect) {
        return true;
      } else if (correctAnswer && answer && !correctAnswer.isCorrect && !answer.isCorrect) {
        return null;
      } else if (correctAnswer && answer && !correctAnswer.isCorrect && answer.isCorrect) {
        return false;
      }
    }
    return null;
  }

  isQuestionCorrect = (lessonElementState, lessonElementId) => {
    let questionCorrect = QuestionFeedbackState.INCORRECT;

    if (!lessonElementState) {
      questionCorrect = QuestionFeedbackState.INCORRECT;
      return QuestionFeedbackState.INCORRECT;
    }

    if (lessonElementState.submittedItemScore >= lessonElementState.maxScore) {
      questionCorrect = QuestionFeedbackState.CORRECT;
    }
    if ((lessonElementState.submittedItemScore > 0) && (lessonElementState.submittedItemScore < lessonElementState.maxScore)) {
      questionCorrect = QuestionFeedbackState.PARTIAL_CORRECT;
    }

    let correctCount = 0;
    if (lessonElementState && lessonElementState.currentResponseAnswer) {
      correctCount = (lessonElementState.currentResponseAnswer.trueCorrectList) ? lessonElementState.currentResponseAnswer.trueCorrectList.length : 0;
    }

    if (questionCorrect === QuestionFeedbackState.PARTIAL_CORRECT && (correctCount <= 0)) {
      questionCorrect = QuestionFeedbackState.INCORRECT;
    }
    return questionCorrect;
  }
}

export default new MultiselectBehavior();
